import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useRef,
  useEffect,
} from "react";

export interface ToastOptions {
  duration?: number | "infinite";
  interactive?: boolean;
}

interface ToastContextType {
  showToast: (content: ReactNode, options?: ToastOptions) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [show, setShow] = useState(false);
  const [content, setContent] = useState<ReactNode>(null);
  const [interactive, setInteractive] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hideToast = useCallback(() => {
    setShow(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showToast = useCallback(
    (content: ReactNode, options?: ToastOptions) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const duration = options?.duration ?? 3000;
      const isInteractive = options?.interactive ?? false;

      setContent(content);
      setInteractive(isInteractive);
      setShow(true);

      // Set timeout only if duration is not infinite
      if (duration !== "infinite") {
        timeoutRef.current = setTimeout(() => {
          setShow(false);
          timeoutRef.current = null;
        }, duration);
      }
    },
    []
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <div className="fixed top-0 left-0 right-0 flex justify-center z-50 px-4 pt-2 pointer-events-none">
        <div
          className={`${
            show ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
          } transform transition-all duration-300 w-full max-w-sm bg-[rgba(255,255,255,0.98)] backdrop-blur-sm p-4 rounded-2xl shadow-lg ${
            interactive ? "pointer-events-auto" : "pointer-events-none"
          }`}
        >
          {content}
        </div>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};
